import { CheckOutlined, EditOutlined } from '@ant-design/icons'
import { Stack, Typography } from '@sureco/design-system'
import {
  Button,
  Card,
  Col,
  Grid,
  Row,
  Skeleton,
  Switch,
} from '@surecompanies/core_components'
import { CopyableValue } from 'components/CopyableValue'
import RHFInput from 'enrollment-platform/components/form/hookform/Input/RHFInput'
import React, { useCallback, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import type { Agent } from 'enrollment-platform/api/partner-benefit-graph/ideon-agents.types'

type ProfileFormValues = {
  firstName: string
  middleName: string
  lastName: string
  nationalProducerNumber: string
  accountPrimary: boolean
}

const emptyProfile: ProfileFormValues = {
  firstName: '',
  middleName: '',
  lastName: '',
  nationalProducerNumber: '',
  accountPrimary: false,
}

const profileFromAgent = (a: Agent | null): ProfileFormValues =>
  a
    ? {
        firstName: a.firstName,
        middleName: a.middleName ?? '',
        lastName: a.lastName,
        nationalProducerNumber: a.nationalProducerNumber,
        accountPrimary: a.accountPrimary,
      }
    : emptyProfile

const ReadField = ({
  label,
  loading,
  children,
}: {
  label: string
  loading: boolean
  children: React.ReactNode
}) => (
  <Stack>
    {/* Matches the edit-mode form label exactly so toggling read↔edit
        doesn't change label weight/color. */}
    <Typography size={14}>{label}</Typography>
    {/* Single 32px-tall container for both states so loading→loaded and
        read↔edit transitions don't shift the Profile card vertically. */}
    <div style={{ display: 'flex', alignItems: 'center', minHeight: 32 }}>
      {loading ? (
        <Skeleton.Input
          active
          style={{ width: '100%', height: 32, minWidth: 0 }}
        />
      ) : (
        <Typography size={16} color="#003146">
          {children}
        </Typography>
      )}
    </div>
  </Stack>
)

export type ProfileCardProps = {
  agent: Agent | null
  loading: boolean
  savingAgent: boolean
  saveAgentProfile: (patch: ProfileFormValues) => Promise<boolean>
}

export default function ProfileCard({
  agent,
  loading,
  savingAgent,
  saveAgentProfile,
}: ProfileCardProps) {
  const screens = Grid.useBreakpoint()
  const isMobile = screens.md === false

  const [editing, setEditing] = useState(false)
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isValid },
  } = useForm<ProfileFormValues>({
    mode: 'onChange',
    defaultValues: emptyProfile,
  })

  useEffect(() => {
    // Don't clobber the user's unsaved changes if the agent reloads (e.g.,
    // background refresh) while they're mid-edit.
    if (agent && !editing) reset(profileFromAgent(agent))
  }, [agent, editing, reset])

  const onSubmit = handleSubmit(async (values) => {
    const ok = await saveAgentProfile(values)
    if (ok) setEditing(false)
  })

  const handleCancel = useCallback(() => {
    reset(profileFromAgent(agent))
    setEditing(false)
  }, [agent, reset])

  return (
    <Card
      title="Profile"
      bordered={false}
      // Ant replaces the body with a multi-line shimmering skeleton when
      // loading — clearer than the per-field skeletons users were reading as
      // empty values.
      loading={loading}
      extra={
        !editing && !loading && agent ? (
          <Button
            type="default"
            icon={<EditOutlined aria-hidden="true" />}
            onClick={() => setEditing(true)}
          >
            Edit
          </Button>
        ) : null
      }
    >
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24}>
          <ReadField label="Ideon Agent ID" loading={loading}>
            <CopyableValue value={agent?.id} />
          </ReadField>
        </Col>
      </Row>

      {editing ? (
        <form onSubmit={onSubmit} noValidate>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Stack>
                <Typography size={14}>First name</Typography>
                <RHFInput
                  name="firstName"
                  control={control}
                  rules={{ required: 'Required' }}
                  autoComplete="given-name"
                  aria-label="First name"
                  aria-required="true"
                />
              </Stack>
            </Col>
            <Col xs={24} md={8}>
              <Stack>
                <Typography size={14}>Middle name (optional)</Typography>
                <RHFInput
                  name="middleName"
                  control={control}
                  autoComplete="additional-name"
                  aria-label="Middle name (optional)"
                />
              </Stack>
            </Col>
            <Col xs={24} md={8}>
              <Stack>
                <Typography size={14}>Last name</Typography>
                <RHFInput
                  name="lastName"
                  control={control}
                  rules={{ required: 'Required' }}
                  autoComplete="family-name"
                  aria-label="Last name"
                  aria-required="true"
                />
              </Stack>
            </Col>

            <Col xs={24} md={12}>
              <Stack>
                <Typography size={14}>NPN (optional)</Typography>
                <RHFInput
                  name="nationalProducerNumber"
                  control={control}
                  inputMode="numeric"
                  autoComplete="off"
                  aria-label="NPN (optional)"
                />
              </Stack>
            </Col>
            <Col xs={24} md={12}>
              <Stack>
                <Typography size={14}>Account primary (optional)</Typography>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 32,
                  }}
                >
                  <Controller
                    name="accountPrimary"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onChange={field.onChange}
                        ref={field.ref}
                        aria-label="Account primary"
                      />
                    )}
                  />
                </div>
              </Stack>
            </Col>
          </Row>

          <Stack
            direction={isMobile ? 'col' : 'row'}
            space={8}
            style={{
              marginTop: 24,
              justifyContent: isMobile ? undefined : 'flex-end',
            }}
          >
            <Button
              onClick={handleCancel}
              disabled={savingAgent}
              block={isMobile}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={savingAgent}
              disabled={!isDirty || !isValid}
              block={isMobile}
            >
              Save
            </Button>
          </Stack>
        </form>
      ) : (
        <>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <ReadField label="First name" loading={loading}>
                {agent?.firstName ?? '—'}
              </ReadField>
            </Col>
            <Col xs={24} md={8}>
              <ReadField label="Middle name" loading={loading}>
                {agent?.middleName || '—'}
              </ReadField>
            </Col>
            <Col xs={24} md={8}>
              <ReadField label="Last name" loading={loading}>
                {agent?.lastName ?? '—'}
              </ReadField>
            </Col>
            <Col xs={24} md={12}>
              <ReadField
                label="National producer number (NPN)"
                loading={loading}
              >
                <CopyableValue value={agent?.nationalProducerNumber} />
              </ReadField>
            </Col>
            <Col xs={24} md={12}>
              <ReadField label="Account primary" loading={loading}>
                {agent?.accountPrimary ? (
                  <CheckOutlined
                    role="img"
                    aria-label="Yes"
                    style={{ color: '#52c41a' }}
                  />
                ) : (
                  'No'
                )}
              </ReadField>
            </Col>
          </Row>
          {/* Reserves vertical space for the Save/Cancel row in edit mode
              so the Profile card height doesn't shift between modes. */}
          <Stack
            direction={isMobile ? 'col' : 'row'}
            space={8}
            style={{
              marginTop: 24,
              justifyContent: isMobile ? undefined : 'flex-end',
              visibility: 'hidden',
            }}
            aria-hidden="true"
          >
            <Button block={isMobile}>Cancel</Button>
            <Button type="primary" block={isMobile}>
              Save
            </Button>
          </Stack>
        </>
      )}
    </Card>
  )
}
